import { instructionLines, resolveActiveRules, RULEBOOK_PLAYTEST_MAX_HISTORY, RULEBOOK_PLAYTEST_MAX_MESSAGE_CHARS, } from "@next-platform/packages-ts-types";
import toRulebookExportEnvelope from "./toRulebookExportEnvelope";
/** Pending lines that fit in one evaluation: the endpoint takes at most this many batch messages, one being the user's. */
export const PLAYTEST_MAX_PENDING_MESSAGES = RULEBOOK_PLAYTEST_MAX_HISTORY - 1;
/** Longest message the playtest endpoint accepts. */
export const PLAYTEST_MAX_MESSAGE_CHARS = RULEBOOK_PLAYTEST_MAX_MESSAGE_CHARS;
const isMessage = (entry) => entry.kind === "message";
const toPlaytestMessage = (message) => ({ role: message.role, content: message.text });
/** Pending messages with text, in order: what the next user message is evaluated together with. */
export function pendingMessages(entries) {
    return entries.filter(isMessage).filter((message) => message.pending && message.text.trim().length > 0);
}
/** Count of every pending line in the transcript, including blank ones, for the pending cap. */
export function pendingLineCount(entries) {
    return entries.filter(isMessage).filter((message) => message.pending).length;
}
/** Already-evaluated messages, oldest first, capped to what the playtest endpoint accepts as `history`. */
export function evaluatedHistory(entries) {
    return entries
        .filter(isMessage)
        .filter((message) => !message.pending)
        .map(toPlaytestMessage)
        .slice(-RULEBOOK_PLAYTEST_MAX_HISTORY);
}
/** Request for evaluating `userText` together with every pending message since the last evaluation. */
export function buildPlaytestEvaluationRequest({ definition, turnState, entries, userText, charName, }) {
    return {
        definition: toRulebookExportEnvelope(definition),
        state: turnState,
        history: evaluatedHistory(entries),
        since_last_evaluation: [...pendingMessages(entries).map(toPlaytestMessage), { role: "user", content: userText }],
        character: { name: charName },
    };
}
/** Appends a pending character message (written by the creator, or a sample reply). */
export function addPendingCharacterMessage({ entries, id, text, isSample = false, }) {
    return [...entries, { kind: "message", id, role: "character", text, pending: true, isSample, evaluationId: null }];
}
/** Appends a pending user message — used to restore a rerun's user line when the rerun fails. */
export function addPendingUserMessage({ entries, id, text, }) {
    return [...entries, { kind: "message", id, role: "user", text, pending: true, isSample: false, evaluationId: null }];
}
/** Edits a pending message; an edited sample reply becomes the creator's own message. */
export function editPendingMessage({ entries, id, text, }) {
    return entries.map((entry) => (isMessage(entry) && entry.id === id && entry.pending ? { ...entry, text, isSample: false } : entry));
}
export function removePendingMessage({ entries, id }) {
    return entries.filter((entry) => !(isMessage(entry) && entry.id === id && entry.pending));
}
/**
 * Records a successful evaluation: the pending messages that were sent become part of it with the text that was
 * sent (edits made while the request ran don't rewrite history), followed by the user message and the evaluation
 * card. Pending messages that weren't sent stay pending.
 */
export function applyPlaytestEvaluation({ entries, sent, definition, evaluationId, userMessageId, userText, before, request, response, }) {
    const sentText = new Map(sent.map((message) => [message.id, message.text]));
    const settled = entries
        .filter((entry) => !(isMessage(entry) && entry.pending && !sentText.has(entry.id) && entry.text.trim().length === 0))
        .map((entry) => {
        const text = isMessage(entry) && entry.pending ? sentText.get(entry.id) : undefined;
        return isMessage(entry) && text !== undefined ? { ...entry, text, pending: false, isSample: false, evaluationId } : entry;
    });
    const appended = [
        { kind: "message", id: userMessageId, role: "user", text: userText, pending: false, isSample: false, evaluationId },
        { kind: "evaluation", id: evaluationId, batch: request.since_last_evaluation ?? [], definition, before, request, response },
    ];
    // Unsent pending lines (e.g. written after a rerun's undo) stay where they are — after the
    // evaluation — rather than being swallowed into it. The anchor is the last settled entry that
    // isn't a pending message: in the normal flow every pending line was sent so that's the end,
    // and a composer-only rerun (no sent lines) still lands before the trailing pending lines.
    let lastSettledIndex = -1;
    settled.forEach((entry, index) => {
        if (!(isMessage(entry) && entry.pending)) {
            lastSettledIndex = index;
        }
    });
    if (lastSettledIndex < 0) {
        return [...appended, ...settled];
    }
    return [...settled.slice(0, lastSettledIndex + 1), ...appended, ...settled.slice(lastSettledIndex + 1)];
}
/** Id of the most recent evaluation: the only one that can be undone. */
export function latestEvaluationId(entries) {
    for (let index = entries.length - 1; index >= 0; index--) {
        const entry = entries[index];
        if (entry?.kind === "evaluation") {
            return entry.id;
        }
    }
    return null;
}
/**
 * Undoes the latest evaluation: its character messages go back to pending and `before` is the state to
 * restore. `userMessageTarget` decides where the evaluated user message goes: "composer" removes it from the
 * transcript and returns it as `userText` for editing; "transcript" keeps it in place as a pending, editable
 * user line (chosen when the composer already holds a different draft). `null` when it isn't the latest
 * evaluation.
 */
export function undoPlaytestEvaluation({ entries, evaluationId, userMessageTarget = "composer", }) {
    const index = entries.findIndex((entry) => entry.kind === "evaluation" && entry.id === evaluationId);
    const evaluation = entries[index];
    if (!evaluation || evaluation.kind !== "evaluation" || latestEvaluationId(entries) !== evaluationId) {
        return null;
    }
    // The composer-origin user message is the LAST user line of the evaluation: earlier ones were
    // pending batch lines sent with it, and they return to pending like every other restored message.
    const userMessage = entries
        .filter(isMessage)
        .filter((message) => message.role === "user" && message.evaluationId === evaluationId)
        .at(-1);
    const restored = entries
        .slice(0, index)
        .filter((entry) => userMessageTarget === "transcript" || entry !== userMessage)
        .map((entry) => isMessage(entry) && entry.evaluationId === evaluationId ? { ...entry, pending: true, isSample: false, evaluationId: null } : entry);
    // Pending lines written after the evaluation stay pending after the restored ones — except unedited
    // sample replies, which are stale now that their evaluation is undone (editing marks isSample: false).
    const later = entries.slice(index + 1).filter((entry) => !(isMessage(entry) && entry.pending && entry.isSample));
    return { entries: [...restored, ...later], batchEntries: restored, before: evaluation.before, userText: userMessage?.text ?? "" };
}
/**
 * Instructions that started (+) or stopped (−) applying between two states, compared by rule key: a level or rule
 * switching counts, the same rule re-rendering with new values (e.g. the clock's current time) does not.
 */
export function diffPlaytestInstructions({ definition, before, after, }) {
    const beforeLines = instructionLines(resolveActiveRules(definition, before));
    const afterLines = instructionLines(resolveActiveRules(definition, after));
    const beforeKeys = new Set(beforeLines.map((line) => line.key));
    const afterKeys = new Set(afterLines.map((line) => line.key));
    return [
        ...afterLines.filter((line) => !beforeKeys.has(line.key)).map((line) => ({ ...line, sign: "+" })),
        ...beforeLines.filter((line) => !afterKeys.has(line.key)).map((line) => ({ ...line, sign: "−" })),
    ];
}
